import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import useAuth from "../hooks/useAuth";

const FavoritesContext = createContext({});

export const FavoritesProvider = ({ children }) => {
  const { auth } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);

  // Fetch saved video IDs when user logs in
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (!auth?.RawToken) {
        setFavoriteIds([]);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user/saved-ids`,
          {
            headers: {
              Authorization: `Bearer ${auth.RawToken}`,
            },
          }
        );

        if (response.data.success && Array.isArray(response.data.savedVideoIds)) {
          setFavoriteIds(response.data.savedVideoIds);
        }
      } catch (error) {
        console.error("Error fetching favorite IDs:", error);
      }
    };

    fetchFavoriteIds();
  }, [auth?.RawToken]);

  const isFavorite = useCallback(
    (videoId) => {
      return favoriteIds.includes(String(videoId));
    },
    [favoriteIds]
  );

  const addFavorite = useCallback(
    async (videoId) => {
      if (!auth?.RawToken) return { success: false, message: "Please sign in to save videos" };

      // Optimistic update
      setFavoriteIds((prev) => {
        if (!prev.includes(String(videoId))) {
          return [...prev, String(videoId)];
        }
        return prev;
      });

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/user/save`,
          { id: videoId },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth.RawToken}`,
            },
          }
        );
        
        if (!response.data.success) {
          // Revert on API failure
          setFavoriteIds((prev) => prev.filter((id) => id !== String(videoId)));
          return { success: false, message: response.data.message || "Could not save video" };
        }
        return { success: true };
      } catch (error) {
        console.error("Save error:", error.response || error);
        // Revert on network error
        setFavoriteIds((prev) => prev.filter((id) => id !== String(videoId)));
        
        let message = "Network error. Could not save video";
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.response?.status === 403 || error.response?.status === 401) {
          message = "Session expired. Please login again.";
        }
        
        return { success: false, message };
      }
    },
    [auth?.RawToken]
  );

  const removeFavorite = useCallback(
    async (videoId) => {
      if (!auth?.RawToken) return { success: false };

      // Optimistic update
      setFavoriteIds((prev) => prev.filter((id) => id !== String(videoId)));

      try {
        // Backend remove endpoint uses 'clipId' in body
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/user/removesaved`,
          { clipId: videoId },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth.RawToken}`,
            },
          }
        );
        
        if (!response.data.success) {
          // Revert on failure
          setFavoriteIds((prev) => [...prev, String(videoId)]);
          return { success: false, message: response.data.message || "Could not remove video" };
        }
        return { success: true };
      } catch (error) {
        console.error("Remove error:", error.response || error);
        // Revert on network error
        setFavoriteIds((prev) => [...prev, String(videoId)]);

        let message = "Network error. Could not remove video";
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.response?.status === 403 || error.response?.status === 401) {
          message = "Session expired. Please login again.";
        }

        return { success: false, message };
      }
    },
    [auth?.RawToken]
  );

  return (
    <FavoritesContext.Provider
      value={{
        favoriteIds,
        isFavorite,
        addFavorite,
        removeFavorite,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
